import { useEffect } from "react";
import { useNavigate } from "react-router-dom";

import { Icon, Span } from "@icg360/design-system";

import { MultiStepFormLayout } from "components/multi-step-form-layout";
import { useSelectedPolicy } from "data/ss-data-hooks";
import { useMultiStepFormState } from "hooks/use-multi-step-form-state";
import {
  WindDefenseContext,
  type WindDefenseContextState,
} from "pages/offer/wind-defense/wind-defense-flow";
import { formatTwoLineAddress, useFlags } from "utils";

import {
  useWindDefenseEnrollment,
  useWindDefenseEnrollmentSteps,
} from "./hooks";
import styles from "./shingle-reseal.module.scss";

export const ContactInformationConfirmation = () => {
  const { data } = useSelectedPolicy();
  const navigate = useNavigate();
  const { state, formNavigate } =
    useMultiStepFormState<WindDefenseContextState>(WindDefenseContext);
  const { windDefenseReduceFlow } = useFlags();
  const { stepLabels, totalSteps, step } = useWindDefenseEnrollmentSteps(state);
  const { enrollWindDefense, loading, error } = useWindDefenseEnrollment(state);

  useEffect(() => {
    if (
      !(
        state?.phone &&
        state?.firstName &&
        state?.lastName &&
        state?.emailAddress
      )
    ) {
      navigate("../policy-confirmation");
    }
  }, [
    navigate,
    state?.emailAddress,
    state?.firstName,
    state?.lastName,
    state?.phone,
  ]);

  if (!data?.propertyAddress) {
    return null;
  }

  const { lineOne, lineTwo } = formatTwoLineAddress(data.propertyAddress, true);

  const onSubmit = async () => {
    const success = await enrollWindDefense();
    if (success) {
      navigate("/my/property-services/wind-defense/registered");
    }
  };

  if (error) {
    throw new Error("error signing up for Shingle Reseal Program");
  }

  return (
    <MultiStepFormLayout
      showLogo={!!windDefenseReduceFlow}
      labels={stepLabels}
      step={step}
      totalSteps={totalSteps}
      heading={
        windDefenseReduceFlow
          ? "Review details and finish enrollment"
          : "Please review and confirm your details"
      }
      buttonProps={{
        primary: { text: "Submit", onPress: onSubmit, loading: loading },
        cancel: { path: "/my/property-services/wind-defense" },
        promptOnCancel: true,
        formNavigate,
      }}
    >
      <div className={styles.mainContent}>
        <div className={styles.iconItemSm}>
          <div className={styles.iconDS}>
            <Icon name="User" size="lg" />
          </div>
          <div>
            <Span bold>
              {state?.firstName} {state?.lastName}
            </Span>
            <div className={styles.subtext}>{state?.emailAddress}</div>
            <div className={styles.subtext}>{state?.phone}</div>
          </div>
        </div>
        <div className={styles.iconItemSm}>
          <div className={styles.iconDS}>
            <Icon name="Home" size="lg" />
          </div>
          <div>
            <Span bold>{lineOne}</Span>
            <br />
            <Span bold>{lineTwo}</Span>
            <div className={styles.subtext}>
              Policy number: {data.platformPolicyId}
            </div>
          </div>
        </div>
      </div>
    </MultiStepFormLayout>
  );
};
